import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import Modal from './Modal';
import { FiThumbsUp } from 'react-icons/fi';
import { Analytics } from '@vercel/analytics/react';

interface InfoModalProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * InfoModal component that explains what the map is and how it works.
 * Uses the Modal component for the UI.
 */
const InfoModal: React.FC<InfoModalProps> = ({ isOpen, onClose }) => {
  const [thumbsUpClicked, setThumbsUpClicked] = useState(false);
  const [showCloseMessage, setShowCloseMessage] = useState(false);
  
  // Reset states when modal closes
  useEffect(() => {
    if (!isOpen) {
      setThumbsUpClicked(false);
      setShowCloseMessage(false);
    }
  }, [isOpen]);

  // Track modal open event with a page view
  useEffect(() => {
    if (isOpen && window.va) {
      // Track page view when modal is opened
      window.va('pageview', { path: '/about-modal' });
      console.log('Page view tracked: /about-modal');
    }
  }, [isOpen]);
  
  // Show close message after 3 seconds when thumbs up is clicked
  useEffect(() => {
    let timer: NodeJS.Timeout;
    
    if (thumbsUpClicked) {
      timer = setTimeout(() => {
        setShowCloseMessage(true);
      }, 3000);
    }
    
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [thumbsUpClicked]);

  const handleThumbsUp = () => {
    if (thumbsUpClicked && showCloseMessage) {
      // If already clicked and showing close message, close the modal
      onClose();
      return;
    }
    
    if (!thumbsUpClicked) {
      setThumbsUpClicked(true);
      // Track thumbs up click with a page view
      if (window.va) {
        window.va('pageview', { path: '/about-modal/thanks' });
        console.log('Page view tracked: /about-modal/thanks');
      }
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="About NHMesh Live">
      <ModalContentWrapper>
        <HeroImageContainer>
          <HeroImage src="/hero.jpeg" alt="Meshtastic Nodes Map Hero" />
        </HeroImageContainer>
        
        <InfoSection>
          <InfoTitle>Welcome to the NHMesh Live Map</InfoTitle>
          <InfoText>
            This is a live map of Meshtastic Nodes on the public channel here in New Hampshire. Meshtastic is an open source, off-grid, 
            decentralized messaging platform that uses inexpensive LoRa radios. You can learn more about it at <a href="https://meshtastic.org">meshtastic.org</a> and at <a href="https://nhmesh.com">NHMesh.com</a>.
          </InfoText>
          
          <InfoSubtitle>How it Works:</InfoSubtitle>
          <InfoText>
            Each dot on the map represents a Meshtastic node. The color indicates if it's online (green) 
            or offline (gray). If you click on a node, you can see detailed information about it,
            including its location, battery status, and telemtery.
          </InfoText>
          <InfoText>
            The last packet heard by HALL (the node that listens for all the packets) will be displayed in purple, if we know it's location. 
            Sometimes, nodes share nodeinfo (not DMs) directly with other nodes and we map those with green lines to show which nodes believe 
            they are neighbors with various other nodes. Nodes are considered online if heard in the last hour. 
          </InfoText>
          
          <InfoSubtitle>About this Project:</InfoSubtitle>
          <InfoText>
            This is a labor of boredom and deep like, so any encouragement is welcome. I'll leave this thumbs up here.
            If you click it, I'll know. If not, I could, but I'll choose not to. Ping me at ada &#x1F170; nhmesh.com if you want to chat.
          </InfoText>
          
          <ThumbsUpContainer onClick={handleThumbsUp} $clicked={thumbsUpClicked}>
            <FiThumbsUp size={24} />
            <ThumbsUpText>
              {thumbsUpClicked ? 'Thanks!' : 'Appreciated!'}
              {showCloseMessage && <CloseMessage> (click to close)</CloseMessage>}
            </ThumbsUpText>
          </ThumbsUpContainer>
        </InfoSection>
      </ModalContentWrapper>
    </Modal>
  );
};

const ModalContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.lg};
`;

const HeroImageContainer = styled.div`
  width: 100%;
  border-radius: ${({ theme }) => theme.radii.md};
  overflow: hidden;
`;

const HeroImage = styled.img`
  width: 100%;
  height: auto;
  object-fit: cover;
  display: block;
`;

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.space.md};
`;

const InfoTitle = styled.h3`
  font-size: ${({ theme }) => theme.fontSizes['2xl']};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.textDark};
  margin: 0;
`;

const InfoSubtitle = styled.h4`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.semibold};
  color: ${({ theme }) => theme.colors.textDark};
  margin: ${({ theme }) => theme.space.md} 0 ${({ theme }) => theme.space.xs};
`;

const InfoText = styled.p`
  font-size: ${({ theme }) => theme.fontSizes.md};
  line-height: 1.5;
  color: ${({ theme }) => theme.colors.text};
  margin: 0;
`;

interface ThumbsUpContainerProps {
  $clicked: boolean;
}

const ThumbsUpContainer = styled.div<ThumbsUpContainerProps>`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.space.sm};
  padding: ${({ theme }) => theme.space.md};
  margin-top: ${({ theme }) => theme.space.md};
  background-color: ${({ $clicked, theme }) => 
    $clicked ? theme.colors.primaryLight : theme.colors.backgroundDark};
  border-radius: ${({ theme }) => theme.radii.lg};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.normal};
  align-self: flex-start;
  
  &:hover {
    background-color: ${({ $clicked, theme }) => 
      $clicked ? theme.colors.primaryLight : theme.colors.primary};
    color: ${({ theme }) => theme.colors.backgroundLight};
  }
`;

const ThumbsUpText = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
`;

const CloseMessage = styled.span`
  font-size: ${({ theme }) => theme.fontSizes.xs};
  opacity: 0.8;
  margin-left: 4px;
`;

export default InfoModal; 